import { inject, Injectable } from '@angular/core';
import { Option } from '@sapphire/result';

import { StorageProvider, StorageService } from '@ps/core-data-access';

import { AllBuyerSettings, CompanySettings } from '../models/settings.model';
import { FeatureEntitlement } from '../models/entitlement.model';
import { PersistUserSettings } from '../models/user-settings-persist.model';
import { CurrencySettings } from '../models/currency-settings.model';
import { Nullable } from '@ps/utils';

const PREFIX = 'ps-';

const KEYS = {
  companySettings: `${PREFIX}company-settings`,
  allBuyerSettings: `${PREFIX}all-buyer-settings`,
  entitlements: `${PREFIX}user-entitlements`,
  currencySettings: `${PREFIX}currency-settings`,
} as const;

@Injectable({ providedIn: 'root' })
export class UserSettingsStorageService {
  private readonly _storage = inject(StorageService);

  public save(
    {
      allBuyerSettings,
      companySettings,
      entitlements,
      currencySettings,
    }: PersistUserSettings,
    rememberMe: boolean,
  ): void {
    const provider = this._provider(rememberMe);
    this._storage.set(KEYS.companySettings, companySettings, provider);
    this._storage.set(KEYS.allBuyerSettings, allBuyerSettings, provider);
    this._storage.set(KEYS.entitlements, entitlements, provider);
    this._storage.set(KEYS.currencySettings, currencySettings, provider);
  }

  public load(): Option<PersistUserSettings> {
    return this._storage
      .get<Partial<CompanySettings>>(KEYS.companySettings)
      .zip(this._storage.get<AllBuyerSettings>(KEYS.allBuyerSettings))
      .zip(this._storage.get<FeatureEntitlement[]>(KEYS.entitlements))
      .zip(this._storage.get<Nullable<CurrencySettings>>(KEYS.currencySettings))
      .map(
        ([
          [[companySettings, allBuyerSettings], entitlements],
          currencySettings,
        ]) => ({
          companySettings,
          allBuyerSettings,
          entitlements,
          currencySettings,
        }),
      );
  }

  private _provider(rememberMe: boolean): StorageProvider {
    return rememberMe ? 'persistent' : 'session';
  }
}
